import { type WalletAccount } from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  filterDuplicates,
  formatWalletAccountId,
} from '@dynamic-labs-sdk/client/core';

import type { WalletConnectWalletProvider } from '../walletConnectWalletProvider.types';
import { splitCaip10Token } from '../splitCaip10Token';

type CreateWalletAccountsForWalletConnectWalletProviderParams = {
  walletProvider: WalletConnectWalletProvider;
};

export const createWalletAccountsForWalletConnectWalletProvider = ({
  walletProvider,
}: CreateWalletAccountsForWalletConnectWalletProviderParams): WalletAccount[] => {
  /**
   * Since each session can have multiple namespaces, we need to find the namespace
   * that corresponds to this specific wallet provider.
   *
   * A WalletConnect namespace is the equivalent of what we refer to as a chain in the SDK,
   * and they have attributed to them the methods, events and networks that are expected
   * to be supported by the wallet application, as well as which accounts are connected.
   */
  const session = walletProvider.getSession();

  const namespace = session.namespaces[walletProvider.namespaceKey];

  assertDefined(
    namespace,
    `Namespace key ${walletProvider.namespaceKey} of wallet provider ${walletProvider.key} not found in its session ${session.topic}`
  );

  const accountAddresses = filterDuplicates(
    namespace.accounts.map((account) => splitCaip10Token(account).address)
  );

  const formatAddress =
    walletProvider.formatWalletAddress ?? (({ address }) => address);

  return accountAddresses.map((address) => ({
    address: formatAddress({ address }),
    chain: walletProvider.chain,
    id: formatWalletAccountId({
      address,
      chain: walletProvider.chain,
      walletProviderKey: walletProvider.key,
    }),
    lastSelectedAt: null,
    verifiedCredentialId: null,
    walletProviderKey: walletProvider.key,
  }));
};
